import { useClickOutside } from '@lib/hooks'
import { type Offset, ProjectActions } from '@octopus-do/octopus-project-core'
import { thunkAddArrow } from '@store/migrationActions/thunkActions/thunkArrowsActions'
import { commitProjectRequest } from '@storeOld/actionCreators'
import {
  clearAttachmentsSelection,
  selectAttachment,
} from '@storeOld/reducers/canvas/canvas'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { useArtboardMode, useDrag } from '@/hooks'
import type { AppState } from '@/types'

export const useDraggablePosition = (id: string, offset: Offset) => {
  const dispatch = useDispatch()

  const commitOnStop = (next: Offset) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.attachments.updatePosition({
          id,
          offset: next,
        })
      )
    )
  }

  const { onStop, onDrag, isDragging } = useDrag(commitOnStop, offset)

  return {
    isDragging,
    onDrag,
    onStop,
  }
}

export const useSelect = (isDragging: boolean, id: string) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  return useCallback(() => {
    if (isDragging) {
      return
    }
    dispatch(selectAttachment(id))
  }, [id, dispatch, isDragging])
}

export const useClearSelect = (selected: boolean, id: string) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const onClickOutside = useCallback(() => {
    dispatch(clearAttachmentsSelection())
  }, [id, dispatch])

  return useClickOutside<HTMLDivElement>(selected, onClickOutside)
}

export const useCreateArrow = (id: string) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const { arrows: isArrowMode } = useArtboardMode()

  return useCallback(() => {
    if (!isArrowMode) {
      return
    }
    dispatch(thunkAddArrow(id, 'attachment'))
  }, [dispatch, id, isArrowMode])
}
